import React                   from 'react';
import { observer }            from 'mobx-react';
import { Table }               from 'antd';
import { Spin }                from 'antd';
import FormattedMessageWrapper from './FormattedMessageWrapper';
import ListFilters             from './ListFilters';
import currentFilters          from 'utils/currentFilters';
import currentSession          from 'utils/currentSession';
import Space                   from './Space';
import PageTitle               from './PageTitle';
import { Model }               from '@mathquis/modelx';

const getFilterActivated = (collectionFilters: any, filtersReferences: any) => {
  let filterActivatedName;

  Object.keys(filtersReferences).forEach(filterName => {
    const filterReferences = filtersReferences[filterName];

    let isFilterActivated = true;

    // on regarde si toutes les références de ce filtre se trouvent sur la collection
    Object.keys(filterReferences).forEach(filterReference => {
      let isFilterReferenceActivated = false;

      const collectionFilterValue = collectionFilters[filterReference];

      if (typeof collectionFilterValue !== 'undefined') {
        // cas 2 : le filtre est sur la collection
        const filterReferenceValue = filterReferences[filterReference];

        if (Array.isArray(filterReferenceValue)) {
          // cas 2.1 : la valeur du filtre est un tableau
          filterReferenceValue.forEach(value => {
            // si on ne trouve pas une valeur dans les valeurs du filtre de la collection
            if (
              Array.isArray(collectionFilterValue) &&
              !!collectionFilterValue.find(v => v === value)
            ) {
              isFilterReferenceActivated = true;
            }
          });
        } else if (
          // cas 2.2 : la valeur du filtre est une chaîne
          // si la valeur n'est pas la même sur le filtre de la collection
          collectionFilterValue === filterReferenceValue
        ) {
          isFilterReferenceActivated = true;
        }
      }

      if (!isFilterReferenceActivated) {
        isFilterActivated = false;
        return;
      }
    });

    if (isFilterActivated) {
      filterActivatedName = filterName;
    }
  });

  return filterActivatedName;
};

interface ListComponentProps {
  collection: any;
  columns: any[];
  columnsFilters?: any;
  defaultSorter?: any;
  fetchData: (pagination: any, filters: any, sorts: any) => Promise<void>;
  filters?: any;
  filtersReferences?: any;
  loading?: boolean;
  onFilterChange?: (value: any) => void;
  onRowClick?: (recordId: any, record: any) => void;
  rawFilters?: Record<string, any>;
  renderTopTableComponent?: () => any;
  rowSelection?: boolean;
  setSelectedRowKeys?: (selectedRowKeys: any) => void;
  setSelectedRows?: (selectedRows: any) => void;
  title?: any;
  topRightWidget?: any;
}

@observer
class ListComponent extends React.Component<ListComponentProps> {
  public static defaultProps = {
    defaultSorter: {},
    filtersReferences: {},
    onFilterChange: () => {},
    onRowClick: () => {},
    rowSelection: true,
    setSelectedRowKeys: () => {},
    setSelectedRows: () => {},
  };

  public state = {
    initialized: false,
  };

  public async componentDidMount() {
    if (!currentFilters.isLoaded) {
      await currentFilters.load(currentSession.user.user_id);
    }

    const filters = currentFilters.getFiltersForPath(window.location.pathname);
    const sorts = currentFilters.getSortsForPath(window.location.pathname);

    // step to remove to filters that could be disabled
    Object.keys(filters).forEach(filterName => {
      const filterValue = filters[filterName];

      // check if filter is in the references and get the reference
      const filterActivated = getFilterActivated(
        { [filterName]: filterValue },
        this.props.filtersReferences,
      );

      if (!filterActivated) {
        return;
      }

      // check if filter is disabled
      Object.keys(this.props.filters).forEach(key => {
        const filtersGroup = this.props.filters[key];

        if (filtersGroup.filters[filterActivated] && filtersGroup.disabled) {
          // filter is disabled, we remove it
          delete filters[filterName];
        }
      });
    });

    await this.props.fetchData({ current: 1 }, filters, sorts.field ? sorts : this.props.defaultSorter);

    this.setState({
      initialized: true,
    });
  }

  render() {
    const {
      collection,
      filters,
      filtersReferences,
      loading,
      onFilterChange,
      onRowClick,
      renderTopTableComponent,
      rowSelection,
      setSelectedRowKeys,
      setSelectedRows,
      title,
      topRightWidget,
    } = this.props;

    const { initialized } = this.state;

    const buildColumns = this.buildColumns();

    const filterActivated = getFilterActivated(collection.filters, filtersReferences);

    let finalRowSelection: any = rowSelection;

    if (finalRowSelection) {
      finalRowSelection = {
        onChange: (selectedRowKeys: any, selectedRows: any) => {
          if (setSelectedRowKeys) setSelectedRowKeys(selectedRowKeys);
          if (setSelectedRows) setSelectedRows(selectedRows);
        },
      };
    }

    return (
      <>
        <PageTitle initialized={initialized} title={title} topRightWidget={topRightWidget} />

        <div style={contentStyle}>
          <Space size={16}>
            {filters && (
              <Spin spinning={loading}>
                <ListFilters
                  filterActivated={filterActivated}
                  filtersGroups={filters}
                  onChange={value => {
                    if (onFilterChange) onFilterChange(value);
                  }}
                />
              </Spin>
            )}

            {renderTopTableComponent && renderTopTableComponent()}

            <Table
              columns={buildColumns}
              dataSource={collection.models}
              loading={collection.isLoading}
              onChange={this.onChange}
              onRow={record => ({
                onClick: () => {
                  if (onRowClick) onRowClick(record.id, record);
                }
              })}
              pagination={{
                current: collection.page,
                defaultCurrent: collection.page,
                hideOnSinglePage: true,
                pageSize: collection.itemsPerPage,
                placement: ['topEnd', 'bottomEnd'],
                total: collection.total,
              }}
              rowKey={model => model.id}
              rowSelection={finalRowSelection}
              showSorterTooltip={false}
            />
          </Space>
        </div>
      </>
    );
  }

  private buildColumn = (column: any) => {
    const { columnsFilters, rawFilters } = this.props;

    return {
      dataIndex: column.property,
      filters:
        columnsFilters && columnsFilters[column.property]
          ? columnsFilters[column.property]
          : undefined,
      filteredValue: rawFilters ? (rawFilters[column.property] || null) : undefined,
      render: (_: string, model: Model) => column.render(model),
      sortDirections: ['descend', 'ascend'],
      sorter: column.sorter,
      sortOrder: this.getSortOrder(column.property),
      title: column.intlTitle ? <FormattedMessageWrapper id={column.intlTitle} /> : null,
      width: column.width,
    };
  };

  private buildColumns = (): any[] => {
    const { columns } = this.props;

    return columns.map(column => this.buildColumn(column));
  };

  private getSortOrder = (columnKey: string) => {
    const { collection } = this.props;

    const sortsNames = Object.keys(collection.sorts);

    const sortNameFound = sortsNames.find(sn => sn === columnKey);

    if (!sortNameFound) {
      return false;
    }

    return `${collection.sorts[sortNameFound]}end`;
  };

  private onChange = async (pagination: any, filters: any, sorter: any) => {
    const sort: Record<string, 'asc' | 'desc'> = {};
    if (sorter.field && sorter.order) {
      sort[sorter.field] = sorter.order === 'ascend' ? 'asc' : 'desc';
    }

    await this.props.fetchData(
      pagination,
      {
        ...this.props.collection.filters,
        ...filters,
      },
      sort,
    );
  };

  public onSearch = (value: string) => {
    const { collection, fetchData } = this.props;

    if (value !== '') {
      collection.setFilter('search', value);
    } else {
      delete collection.filters.search;
    }

    fetchData({ current: 1 }, collection.filters, collection.sorts);
  };
}

const contentStyle = {
  background: '#fff',
  boxShadow: 'rgba(0,0,0,0.15) 0 2px 8px',
  minHeight: 360,
  padding: 24,
};

export default ListComponent;
